// components/RecommendationStep.tsx
import React from 'react';
import { SeverityLevel } from '../utils/asthmaTypes';
import { BaseRecommendations, BaseRecommendationScenario } from '../BaseRecommendations';

interface RecommendationStepProps {
  step: 1 | 2 | 3;
  activeSeverity: SeverityLevel | null;
  secondRoundStarted: boolean;
  thirdRoundStarted: boolean;
  improvedFromSevere: boolean;
  totalDosesGiven: number;

  dosesAtStartOfRound2?: number;
  dosesAtStartOfRound3?: number;

  onStartSecondRound: () => void;
  onStartThirdRound: () => void;
  onExit: () => void;
}

const getScenarioForStep = (
  props: RecommendationStepProps
): BaseRecommendationScenario | null => {
  const {
    step,
    activeSeverity,
    secondRoundStarted,
    thirdRoundStarted,
    improvedFromSevere,
    totalDosesGiven,
    dosesAtStartOfRound2,
    dosesAtStartOfRound3,
  } = props;

  if (!activeSeverity) return null;

  // global rule to improve from severe
  if (improvedFromSevere && step !== 1) {
    return 'IMPROVED_FROM_SEVERE';
  }

  // === First Round ===
  if (step === 1) {
    if (
      (activeSeverity === 'MILD' || activeSeverity === 'MODERATE') &&
      improvedFromSevere &&
      !secondRoundStarted
    ) {
      return 'IMPROVED_FROM_SEVERE';
    }
    if (activeSeverity === 'MILD') {
      if (totalDosesGiven > 1) {
        return 'MODERATE_TO_MILD';
      }
      return 'MILD_DEFAULT';
    }

    if (activeSeverity === 'MODERATE') {
      if (totalDosesGiven === 1) {
        return 'MILD_TO_MODERATE';
      }
      return 'MODERATE_DEFAULT';
    }

    if (activeSeverity === 'SEVERE' && secondRoundStarted)
      return 'SEVERE_AFTER_SECOND_ROUND';

    if (activeSeverity === 'SEVERE') {
      if (totalDosesGiven === 1) return 'MILD_TO_SEVERE';
      return 'SEVERE_DEFAULT';
    }
  }

  // === Second Round ===
  if (step === 2) {
    if (improvedFromSevere) return 'IMPROVED_FROM_SEVERE';
    if (activeSeverity === 'MILD') return 'MODERATE_TO_MILD';

    if (activeSeverity === 'SEVERE' && !thirdRoundStarted) {
      if (dosesAtStartOfRound2 === 1) {
        return 'MODERATE_TO_SEVERE';
      }
      if (dosesAtStartOfRound2 === 3) {
        return 'MODERATE_TO_SEVERE';
      }
      return 'SEVERE_AFTER_SECOND_ROUND';
    }

    if (activeSeverity === 'MODERATE') return 'MODERATE_DEFAULT';
  }

  // === Third Round ===
  if (step === 3) {
    if (improvedFromSevere) return 'IMPROVED_FROM_SEVERE';

    if (activeSeverity === 'MILD') return 'MODERATE_TO_MILD';

    if (activeSeverity === 'SEVERE') {
      if (dosesAtStartOfRound3 === 4 || dosesAtStartOfRound3 === 6) {
        return 'MODERATE_TO_SEVERE';
      }
      return 'SEVERE_FINAL';
    }

    if (activeSeverity === 'MODERATE') return 'MODERATE_DEFAULT';
  }

  return null;
};

const RecommendationStep: React.FC<RecommendationStepProps> = (props) => {
  const { step, activeSeverity, onStartSecondRound, onStartThirdRound, onExit } = props;
  const scenario = getScenarioForStep(props);

  if (!scenario || !activeSeverity) return null;


  const handleNextAction = step === 2 ? onStartThirdRound : onStartSecondRound;

  return (
    <BaseRecommendations
      scenario={scenario}
      activeSeverity={activeSeverity}
      onStartSecondRound={handleNextAction}
      onStartThirdRound={onStartThirdRound}
      onExit={onExit}
    />
  );
};

export default RecommendationStep;